import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { Company } from '@/features/multi-company/types/company.types';
import { companyService } from '@/features/multi-company/services/companyService';
import { employeeCompanyService } from '@/features/multi-company/services/employeeCompanyService';
import { getStoredCompany, setStoredCompany, clearStoredCompany } from '@/features/multi-company/utils/company-storage';

interface CompanyContextType {
  currentCompany: Company | null;
  availableCompanies: Company[];
  isLoading: boolean;
  error: string | null;
  switchCompany: (companyId: string) => Promise<void>;
  refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

interface CompanyProviderProps {
  children: ReactNode;
  employeeId?: string; // Optional - defaults to 'current-user'
}

export function CompanyProvider({ children, employeeId = 'current-user' }: CompanyProviderProps) {
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, [employeeId]);

  async function loadCompanies() {
    try {
      setIsLoading(true);
      setError(null);

      // Get all companies
      const allCompanies = await companyService.getCompanies();

      // Get employee's company access
      const employeeCompanyIds = await employeeCompanyService.getEmployeeCompanies(employeeId);

      // Filter to only companies the employee has access to
      const accessibleCompanies = allCompanies.filter((c) =>
        employeeCompanyIds.includes(c.id)
      );

      setAvailableCompanies(accessibleCompanies);

      // Determine which company to load
      let companyToLoad: Company | null = null;

      // 1. Try to load from session storage
      const storedCompanyId = getStoredCompany();
      if (storedCompanyId) {
        companyToLoad = accessibleCompanies.find((c) => c.id === storedCompanyId) || null;
      }

      // 2. If not in storage, load default company
      if (!companyToLoad) {
        const defaultCompanyId = await employeeCompanyService.getDefaultCompany(employeeId);
        if (defaultCompanyId) {
          companyToLoad = accessibleCompanies.find((c) => c.id === defaultCompanyId) || null;
        }
      }

      // 3. If still no company, load first available
      if (!companyToLoad && accessibleCompanies.length > 0) {
        companyToLoad = accessibleCompanies[0];
      }

      if (companyToLoad) {
        setCurrentCompany(companyToLoad);
        setStoredCompany(companyToLoad.id);
      } else {
        setError('No companies available');
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
      setError('Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  }

  async function switchCompany(companyId: string) {
    try {
      setIsLoading(true);
      setError(null);

      const company = availableCompanies.find((c) => c.id === companyId);
      if (!company) {
        throw new Error('Company not found or access denied');
      }

      setCurrentCompany(company);
      setStoredCompany(companyId);
    } catch (err) {
      console.error('Failed to switch company:', err);
      setError('Failed to switch company');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  async function refreshCompanies() {
    await loadCompanies();
  }

  const value: CompanyContextType = {
    currentCompany,
    availableCompanies,
    isLoading,
    error,
    switchCompany,
    refreshCompanies,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany(): CompanyContextType {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
